<template>
	<div
		v-if="confirm && !modal"
		class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-3 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-mabey-950 focus:ring-offset-2"
		@click.stop="emit('click')">
		<label
			v-if="files.length == 0"
			:for="name"
			:disabled="disabled"
			class="my-5 block text-sm text-gray-400">
			<slot></slot>
		</label>

		<file-list v-else :files="files" @remove="(files: number[]) => removeFiles(files)">
			<label
				v-if="files.length > 0 && multiple"
				:for="name"
				data-testid="add-more"
				class="w-full bg-white ring-1 ring-mabey-950 text-mabey-950 rounded-sm text-sm py-2">
				<app-icon :icon="['fas', 'fa-plus']" class="mr-1" />
				Add more
			</label>

			<app-button
				v-if="files.length > 0 && save"
				class="w-full"
				data-testid="save-button"
				@click="save">
				Upload
			</app-button>
		</file-list>
	</div>

	<div v-else>
		<label
			:disabled="disabled"
			class="cursor-pointer"
			:class="classes"
			:for="name"
			data-testid="label"
			@click="emit('click')">
			<font-awesome-icon v-if="icon" :icon="icon" class="mr-1 text-inherit" />
			<slot></slot>
		</label>

		<Confirm
			v-if="confirm"
			id="confirm-files"
			title="Confirm Files"
			theme="info"
			:open="dialog"
			@confirmed="confirmFiles">
			<file-list :files="files" @remove="(files: number[]) => removeFiles(files)" />
		</Confirm>
	</div>

	<input
		:id="name"
		ref="fileInput"
		:name="name"
		:disabled="disabled"
		type="file"
		:accept="accept"
		:multiple="multiple"
		style="display: none"
		data-testid="input"
		@change="onFileChange" />
</template>

<script setup lang="ts">
	import { ref, computed, watch, onMounted } from 'vue'
	import Confirm from '@/components/partials/dialogs/Confirm.vue'
	import FileList from './partials/FileList.vue'
	import type { PreviewFile } from '@/data/interfaces/preview-file'

	const fileInput = ref()
	const files = ref<PreviewFile[]>([])
	const dialog = ref(false)

	const props = withDefaults(
		defineProps<{
			value?: File[]
			name: string
			icon?: string
			size?: 'small' | 'medium' | 'large'
			theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'outline'
			disabled?: boolean
			multiple?: boolean
			accept?: string
			confirm?: boolean
			modal?: boolean
			save?: Function
		}>(),
		{
			value: undefined,
			icon: undefined,
			size: 'medium',
			theme: 'primary',
			disabled: false,
			multiple: false,
			accept: 'image/*',
			confirm: false,
			modal: false,
			save: undefined
		}
	)

	const classes = computed(() => ({
		'px-3 py-2 rounded text-sm focus:ring-2 outline-none': true,
		'bg-mabey-950 hover:bg-mabey-500 focus:ring-mabey-700 text-white': props.theme == 'primary',
		'bg-cyan-400 hover:bg-mabey-300 focus:ring-mabey-300 text-mabey-950':
			props.theme == 'secondary',
		'bg-green-500 hover:bg-green-300 focus:ring-green-300 text-white': props.theme == 'success',
		'bg-mabey-600 hover:bg-mabey-300 focus:ring-mabey-300 text-white hover:text-mabey-950':
			props.theme == 'info',
		'bg-yellow-500 hover:bg-yellow-300 focus:ring-yellow-300 text-white hover:text-yellow-900':
			props.theme == 'warning',
		'bg-red-600 hover:bg-red-500 focus:ring-red-500 text-white': props.theme == 'danger',
		'bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50 text-gray-900':
			props.theme == 'outline',
		'px-4 py-3': props.size === 'large',
		'px-2 py-0.5 text-xs': props.size === 'small',
		'opacity-50 cursor-not-allowed': props.disabled
	}))

	function onFileChange(event: Event) {
		const input = event.target as HTMLInputElement
		if (!input.files || input.files.length == 0) return Promise.resolve()

		return updatePreviews(input.files).then(() => {
			emit('file-change', files.value)
			fileInput.value.value = ''
		})
	}

	async function updatePreviews(f: File[] | FileList) {
		const response = await mapPreviews(f)

		if (props.multiple && files.value.length > 0) {
			response.forEach((file: File) => {
				files.value.push(file)
			})
		} else {
			files.value = response
		}

		if (props.confirm && props.modal) {
			dialog.value = true
		}

		return Promise.resolve()
	}

	/**
	 * Process files & create image previews
	 * @param files FileList
	 * @returns Promise
	 */
	async function mapPreviews(files: File[] | FileList): Promise<PreviewFile[]> {
		const promises = Array.from(files).map((f): Promise<PreviewFile> => {
			return new Promise((resolve) => {
				const file = f as PreviewFile

				if (file.type.startsWith('image/')) {
					const reader = new FileReader()

					reader.onload = (e) => {
						file.preview = e?.target?.result as string | null
						resolve(file)
					}
					reader.readAsDataURL(file)
				} else {
					// Resolve if not an image
					resolve(file)
				}
			})
		})

		return Promise.all(promises)
	}

	function confirmFiles() {
		emit('file-change', files.value)
		dialog.value = false
	}

	function removeFiles(indexes: number[]) {
		indexes.sort((a, b) => b - a)

		indexes.forEach((index: number) => {
			files.value.splice(index, 1)
		})

		emit('file-change', files.value)
	}

	const emit = defineEmits(['click', 'file-change', 'update:modelValue'])

	watch(
		() => props.value,
		(newVal) => {
			if (newVal) {
				files.value = []
				updatePreviews(newVal)
			}
		}
	)

	onMounted(() => {
		if (props.value) {
			updatePreviews(props.value)
		}
	})
</script>
